import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, AlertTriangle, ArrowLeftRight, ExternalLink, Sparkles, Check, Send, Scissors } from "lucide-react";
import { api, ApiError, type BlackoutWindowView, type CalendarSlotView, type SocialPlatform, type SocialCheckIssue, type RegenerateJobView, type DeckGoalKey } from "../../lib/api.js";
import { SlideThumb } from "../../components/social/SlideThumb.js";
import { DECK_GOAL_OPTIONS, CONTENT_SLIDE_CHOICES, isDeckFormat, ROLE_HE } from "../../components/social/deckGoals.js";
import { ARTICLE_GAP_MINUTES } from "./calendarRules.js";
import { useDismiss } from "../../components/ui/useDismiss.js";

/**
 * Edit one post: its time, and its words.
 *
 * WHY THIS EXISTS. `PATCH /api/social/variants/:id` has always accepted
 * `scheduledFor`, and nothing in the platform ever sent it one. The slot times
 * are an editorial table in `keywords/src/social/slots.ts` — evening for
 * carousels, morning for link posts, midday for stories — and the only way to
 * disagree with that table for a single post was to edit the source.
 *
 * THE DATE IS PART OF THE POST WHEN THE POST IS IN POSTIZ. Postiz has no
 * endpoint that edits one, so moving it there is a withdraw and a re-create, on
 * the same path a normal send takes. That is why this dialog says which of the
 * two things it is about to do instead of showing one generic Save: moving a
 * variant that only exists here is free and reversible, and moving one that
 * Postiz holds briefly takes the post out of the calendar and puts a new one
 * back. Those deserve different sentences.
 *
 * THE COPY EDITOR IS THE SAME PATCH. A full one already existed on the
 * pipeline's Social tab, per article, and nothing led there from the calendar —
 * so seeing a bad caption on Thursday meant finding which article it came from
 * first. The date and the words go up in ONE request on purpose: the server
 * republishes once for the whole change, and sending them separately would
 * withdraw and re-create the post twice.
 */

const TZ = "Asia/Jerusalem";

const PLATFORM_HE: Record<SocialPlatform, string> = {
  facebook: "פייסבוק", instagram: "אינסטגרם", tiktok: "טיקטוק", youtube: "יוטיוב",
};

const FORMAT_HE: Record<string, string> = {
  feed_link: "פוסט+לינק", feed_image: "תמונה", carousel: "קרוסלה", reel: "ריל",
  story: "סטורי", video: "וידאו", photo_post: "תמונות", short: "שורט", long_video: "סרטון ארוך",
};

/**
 * How far `tz` is ahead of UTC at a given instant, in milliseconds.
 *
 * The same trick the server uses in `slots.ts`, and for the same reason: Israel
 * is UTC+2 for half the year and UTC+3 for the other half, so a wall clock the
 * operator types is not an instant until a zone is applied to it AT THAT DATE.
 * Doing this with the browser's own timezone would be right only for an operator
 * sitting in Israel, and silently wrong on a laptop that travelled.
 */
function zoneOffsetMs(instant: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(instant);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
  return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"))
    - instant.getTime() + (instant.getTime() % 1000);
}

/** "2026-09-14" + "20:00" as Israel wall clock → the instant it names. */
function israelWallClockToIso(day: string, time: string): string {
  const [y, m, d] = day.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const asUtc = Date.UTC(y!, (m ?? 1) - 1, d!, hh!, mm!);
  // Two passes: the offset depends on the instant, and the instant depends on
  // the offset. One correction lands it except exactly at a DST boundary, where
  // the second settles it.
  //
  // Checked across a DST year: 14 Sep 20:00 → 17:00Z (UTC+3), 14 Dec 20:00 →
  // 18:00Z (UTC+2), and the changeover days round-trip. The one input that does
  // not come back unchanged is a wall clock that DOES NOT EXIST — 02:30 on the
  // spring-forward morning, which Israel skips entirely — and that resolves to
  // 03:30. That is the correct answer to an impossible question, not a bug to
  // fix; no slot in the table is anywhere near 02:00.
  let guess = asUtc;
  for (let i = 0; i < 2; i++) guess = asUtc - zoneOffsetMs(new Date(guess), TZ);
  return new Date(guess).toISOString();
}

const israelParts = (iso: string) => {
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date(iso));
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(new Date(iso));
  return { day, time };
};

const humanIl = (iso: string) =>
  new Intl.DateTimeFormat("he-IL", {
    timeZone: TZ, weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(new Date(iso));

export function MoveSlotDialog({
  slot, siblings, blackouts = [], postizAppUrl, onClose, onMoved, onRefresh,
}: {
  slot: CalendarSlotView;
  /** Every other slot in view, to warn about landing on top of one. */
  siblings: CalendarSlotView[];
  /** Shabbat / chag / memorial-day windows in view, to warn before the server refuses. */
  blackouts?: BlackoutWindowView[];
  /** Base of the Postiz UI, from the calendar response. Null when unconfigured. */
  postizAppUrl: string | null;
  onClose: () => void;
  /** Applied and nothing left to read: refresh the calendar and close. */
  onMoved: () => void;
  /** Applied but there is something on screen worth reading: refresh only. */
  onRefresh: () => void;
}) {
  const dismiss = useDismiss(onClose);
  const initial = israelParts(slot.scheduledFor);
  const [day, setDay] = useState(initial.day);
  const [time, setTime] = useState(initial.time);
  const [body, setBody] = useState(slot.body);
  const [title, setTitle] = useState(slot.title ?? "");
  const [tags, setTags] = useState(slot.hashtags.join(" "));
  // Instagram Reels only. Default ON for a new post, so this reads the row.
  const [trialReel, setTrialReel] = useState(slot.trialReel);
  const [error, setError] = useState<string | null>(null);
  const [postizNote, setPostizNote] = useState<string | null>(null);
  const [issues, setIssues] = useState<SocialCheckIssue[] | null>(null);
  const [steer, setSteer] = useState("");
  // Structural asks for a deck post. Empty = let the model decide, as before.
  const [goal, setGoal] = useState<DeckGoalKey | "">("");
  const [contentSlides, setContentSlides] = useState<number>(0);
  // Bumped after a rewrite or re-cut, so the frame previews re-render.
  const [nonce, setNonce] = useState(0);
  const [recutNote, setRecutNote] = useState<string | null>(null);
  const isDeck = isDeckFormat(slot.format) && slot.slideCount > 0;
  const [regenNote, setRegenNote] = useState<string | null>(null);
  // Two presses, not a browser confirm(): the first says what will happen, the
  // second does it. Taking a post out of Postiz is not something to do by a
  // stray click, and a native dialog does not say which of the two modes it is.
  const [armed, setArmed] = useState<"unschedule" | "remove" | null>(null);
  const [sendArmed, setSendArmed] = useState(false);
  const [sentNote, setSentNote] = useState<string | null>(null);

  const nextIso = useMemo(() => {
    try { return israelWallClockToIso(day, time); } catch { return null; }
  }, [day, time]);

  const timeChanged = Boolean(nextIso) && Date.parse(nextIso!) !== Date.parse(slot.scheduledFor);
  const copyChanged =
    body !== slot.body ||
    title !== (slot.title ?? "") ||
    tags.trim() !== slot.hashtags.join(" ");
  const trialChanged = trialReel !== slot.trialReel;
  const changed = timeChanged || copyChanged || trialChanged;
  const intoThePast = Boolean(nextIso) && Date.parse(nextIso!) < Date.now();

  /**
   * Shabbat, chag and memorial days are closed to posting, and the server
   * refuses a move into one (409 `posting_blackout`). Known here for the
   * fortnight on screen so the button is off before the round trip; a date
   * outside it is caught by the server, which sends back the time to offer.
   */
  const blackedOut = useMemo(() => {
    if (!nextIso || !timeChanged) return null;
    const t = Date.parse(nextIso);
    return blackouts.find((w) => Date.parse(w.startIso) <= t && t < Date.parse(w.endIso)) ?? null;
  }, [nextIso, timeChanged, blackouts]);
  const [suggested, setSuggested] = useState<string | null>(null);
  const [seasonalNote, setSeasonalNote] = useState<string | null>(null);

  /**
   * The same rule the calendar rings in amber and the fill planner never breaks:
   * a post of ANOTHER article, on any platform, less than three hours away.
   * Posts of this same article sharing a moment are the plan, not a clash.
   * Warned about, NOT blocked: an operator moving a post deliberately has a
   * reason, and this screen is the place to make sure it was not an accident.
   */
  const clash = useMemo(() => {
    if (!nextIso) return null;
    return siblings.find(
      (s) =>
        s.id !== slot.id &&
        s.packId !== slot.packId &&
        Math.abs(Date.parse(s.scheduledFor) - Date.parse(nextIso)) / 60000 < ARTICLE_GAP_MINUTES,
    ) ?? null;
  }, [nextIso, siblings, slot]);

  const move = useMutation({
    mutationFn: async () => {
      setError(null);
      setPostizNote(null);
      setIssues(null);
      // ONE patch. The server republishes once for whatever changed, so sending
      // the time and the words together is a single withdraw-and-re-create in
      // Postiz rather than two.
      return api.updateSocialVariant(slot.id, {
        ...(timeChanged ? { scheduledFor: nextIso! } : {}),
        ...(copyChanged
          ? {
              body,
              title: title.trim() === "" ? null : title,
              hashtags: tags.split(/[\s,]+/).map((t) => t.replace(/^#/, "")).filter(Boolean),
            }
          : {}),
        ...(trialChanged ? { trialReel } : {}),
      });
    },
    onSuccess: (r) => {
      // The copy names a holiday this post no longer lands in. Kept on screen:
      // the fix is an edit, and closing would hide the only place saying so.
      if (r.seasonal && r.seasonal.length > 0) {
        setSeasonalNote(`הטקסט מזכיר ${r.seasonal.map((i) => `"${i.term}"`).join(", ")}, אבל הפוסט יוצא מחוץ ל${[...new Set(r.seasonal.flatMap((i) => i.labelsHe))].join("/")}. כדאי לערוך או לכתוב מחדש.`);
        onRefresh();
        return;
      }
      // Re-run because the copy changed. Shown whether they pass or not: a
      // caption that got LONGER and is still inside the limit is worth a green
      // line, and one that broke a limit must not be discovered at send time.
      if (r.checks && r.checks.issues.length > 0) setIssues(r.checks.issues);
      // The Postiz half is reported separately BECAUSE IT CAN FAIL ON ITS OWN.
      // The row moved either way; whether Postiz followed is the part an
      // operator would otherwise find out by watching a post go out at the old
      // time. `withdrew` with no new id means the slot is empty over there.
      if (r.postiz && r.postiz.ok === false) {
        setPostizNote(`התאריך עודכן אצלנו, אבל לא בפוסטיז: ${r.postiz.error}`);
        return;
      }
      if (r.postiz?.ok) {
        setPostizNote(`עודכן גם בפוסטיז${r.postiz.withdrew ? " (הפוסט הישן נמשך ונוצר חדש)" : ""}.`);
      }
      // The calendar refreshes either way. The dialog stays open when the checks
      // found something, because closing it would throw away the only place that
      // says the caption is now too long.
      const worthReading = Boolean(r.checks && r.checks.issues.length > 0) || Boolean(r.postiz);
      if (worthReading) { onRefresh(); return; }
      onMoved();
    },
    onError: (e) => {
      setError((e as Error).message);
      if (e instanceof ApiError && e.data?.code === "posting_blackout" && typeof e.data.nextAllowed === "string") {
        setSuggested(e.data.nextAllowed);
      }
    },
  });

  /**
   * Re-write the post with the model, from the article it came from.
   *
   * The result lands in the FIELDS, not straight into the database's last word:
   * the server has already written it (a re-write is not a preview), but the
   * editor is refilled so the operator reads the new copy in the same box they
   * would edit it in, and can keep going from there.
   */
  /**
   * The rewrite runs on the server in the background and this polls it. It used
   * to wait inside the request, and a rewrite that needed a second attempt ran
   * past the gateway's 60 seconds: a 504 on screen, a finished and paid rewrite
   * on the server, and a button that invited paying for another one.
   */
  const [regenJob, setRegenJob] = useState<RegenerateJobView | null>(null);
  const regenRunning = regenJob?.status === "running";

  const applyRegenResult = (job: RegenerateJobView, variant?: { body: string; title: string | null; hashtags: string[] }) => {
    if (job.status === "error" || (job.outcome !== null && job.outcome !== 200)) {
      setError(job.error ?? "הכתיבה מחדש נכשלה");
      if (job.issues.length > 0) setIssues(job.issues);
      return;
    }
    if (variant) {
      setBody(variant.body);
      setTitle(variant.title ?? "");
      setTags(variant.hashtags.join(" "));
    }
    setSteer("");
    setNonce((n) => n + 1);
    setRegenNote(`נכתב מחדש · $${job.costUsd.toFixed(3)}`);
    if (job.issues.length > 0) setIssues(job.issues);
    if (job.postiz && job.postiz.ok === false) setPostizNote(`הטקסט עודכן אצלנו, אבל לא בפוסטיז: ${job.postiz.error}`);
    onRefresh();
  };

  // RE-ATTACH ON OPEN: a rewrite started before the dialog was closed is shown,
  // rather than offering the button again.
  useEffect(() => {
    void api.regenerateStatus(slot.id).then((r) => { if (r.job?.status === "running") setRegenJob(r.job); }).catch(() => {});
  }, [slot.id]);

  useEffect(() => {
    if (!regenRunning) return;
    const t = setInterval(() => {
      void api.regenerateStatus(slot.id).then((r) => {
        if (!r.job) return;
        setRegenJob(r.job);
        if (r.job.status !== "running") applyRegenResult(r.job, r.variant);
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenRunning, slot.id]);

  const regenerate = useMutation({
    mutationFn: () => {
      setError(null); setIssues(null); setRegenNote(null);
      return api.regenerateSocialVariant(
        slot.id,
        steer.trim() || undefined,
        isDeck ? { ...(goal ? { goal } : {}), ...(contentSlides ? { contentSlides } : {}) } : undefined,
      );
    },
    onSuccess: (r) => setRegenJob(r.job),
    // 409 = a rewrite of this post is already running. Attach to it.
    onError: (e) => {
      void api.regenerateStatus(slot.id).then((r) => {
        if (r.job?.status === "running") setRegenJob(r.job); else setError((e as Error).message);
      }).catch(() => setError((e as Error).message));
    },
  });

  /**
   * A story re-cut from its pack's carousel. Free, and near-instant: the frames
   * already exist there, the story just took fewer of them.
   */
  const recut = useMutation({
    mutationFn: () => { setError(null); setRecutNote(null); return api.recutStory(slot.id); },
    onSuccess: (r) => {
      setNonce((n) => n + 1);
      setRecutNote(
        `נחתך מחדש מהקרוסלה: ${r.before} ← ${r.after} פריימים עם תוכן` +
        (r.postiz ? (r.postiz.ok ? " · עודכן גם בפוסטיז" : ` · לא עודכן בפוסטיז: ${r.postiz.error}`) : ""),
      );
      onRefresh();
    },
    onError: (e) => setError((e as Error).message),
  });

  /**
   * GATE. `approved` is what the drip selects, so this is the moment a post
   * stops being a draft and becomes something that will be handed to Postiz on
   * its slot. It lives here because the calendar is where an operator looks at
   * what is going out — the only approve button used to be in the pipeline, one
   * article at a time, which is why nothing was ever approved.
   */
  const approve = useMutation({
    mutationFn: () => {
      setError(null);
      return api.updateSocialVariant(slot.id, { status: "approved" });
    },
    // Stays open: the next step after approving is sending, and it is on this
    // same screen. Closing here sent the operator to find the post again.
    onSuccess: () => { onRefresh(); },
    onError: (e) => setError((e as Error).message),
  });

  /**
   * Send to Postiz APPROVED: it publishes at its slot with no step in Postiz.
   * Two presses, and the second one names the channel and the moment, because
   * this is the button that ends with the post live under the brand.
   */
  const sendScheduled = useMutation({
    mutationFn: () => { setError(null); return api.sendSocialVariant(slot.id, "schedule"); },
    onSuccess: (r) => {
      setSendArmed(false);
      if (!r.ok) { setError(r.error ?? "השליחה נכשלה"); return; }
      setSentNote(`נשלח לפוסטיז ומתוזמן לפרסום · ${r.uploaded} קבצים הועלו`);
      onRefresh();
    },
    onError: (e) => { setSendArmed(false); setError((e as Error).message); },
  });
  const slotInFuture = Date.parse(slot.scheduledFor) > Date.now() + 5 * 60 * 1000;
  const canSend = slot.status === "approved" && !slot.postizPostId && !changed && slotInFuture;

  const takeOff = useMutation({
    mutationFn: (mode: "unschedule" | "remove") => {
      setError(null);
      return api.unscheduleSocialVariant(slot.id, mode);
    },
    onSuccess: () => onMoved(),
    onError: (e) => { setArmed(null); setError((e as Error).message); },
  });

  // A published post is not movable, and this says so instead of offering a
  // control that would 409. The refusal lives on the server too — the UI is the
  // explanation, not the guard.
  const published = slot.status === "posted";

  return (
    <div {...dismiss} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      {/* A column capped at the viewport: header and actions stay put, the body
          between them scrolls. Without the cap the dialog grew past the screen
          once it carried the caption, the CTA, the rewrite box and the preview,
          and the save and send buttons were simply below the fold. */}
      <div className={`flex max-h-[92vh] w-full ${isDeck ? "max-w-3xl" : "max-w-md"} flex-col overflow-hidden rounded-xl bg-white shadow-xl`}>
        <header className="flex shrink-0 items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold">עריכת פוסט</h2>
          <button onClick={onClose} className="text-sm text-muted hover:text-ink">סגירה</button>
        </header>

        <div className="min-h-0 grow space-y-3 overflow-y-auto p-4 text-sm">
          <div>
            <p className="font-medium">{slot.label ?? slot.campaign}</p>
            <p className="text-xs text-muted">
              {PLATFORM_HE[slot.platform]} · {FORMAT_HE[slot.format] ?? slot.format}
              {" · כרגע "}{humanIl(slot.scheduledFor)}
            </p>
          </div>

          {/* THE POST ITSELF, frame by frame. Postiz draws a 4-frame story and an
              8-slide carousel identically, and the 30.9 Helicobacter story was
              read as "the carousel is thin" for exactly that reason. The same
              renderer as the export, so this is what goes out. */}
          {isDeck && (
            <section className="rounded-lg border border-line p-2.5">
              <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span className="font-medium">{FORMAT_HE[slot.format] ?? slot.format}</span>
                <span className="text-muted">
                  · {slot.slideCount} {slot.format === "story" ? "פריימים" : "שקופיות"}
                  {slot.deck && <> · {slot.deck.content} עם תוכן · מטרה: {slot.deck.goalLabelHe}</>}
                </span>
                <span className="grow" />
                <button onClick={() => setNonce((n) => n + 1)} className="text-[11px] text-muted underline decoration-dotted hover:text-ink">
                  רענון תצוגה
                </button>
              </div>
              <div className="flex flex-wrap gap-2" dir="rtl">
                {Array.from({ length: slot.slideCount }, (_, i) => (
                  <div key={i} className="shrink-0 text-center">
                    <SlideThumb
                      variantId={slot.id}
                      index={i}
                      scale={{}}
                      width={slot.format === "story" || slot.format === "photo_post" ? 96 : 120}
                      nonce={nonce}
                      format={slot.format === "story" || slot.format === "photo_post" ? "story" : "portrait"}
                    />
                    <span className="mt-0.5 block text-[10px] text-muted">{i + 1} · {ROLE_HE[slot.slideRoles[i] ?? ""] ?? slot.slideRoles[i]}</span>
                  </div>
                ))}
              </div>
              {slot.deck?.thin && (
                <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                  <p className="font-medium">הפוסט רזה:</p>
                  <ul className="list-disc pr-4">
                    {slot.deck.reasonsHe.map((r, n) => <li key={n}>{r}</li>)}
                  </ul>
                  {slot.format === "story" && !published && (
                    <button
                      onClick={() => recut.mutate()}
                      disabled={recut.isPending}
                      className="mt-1.5 flex items-center gap-1.5 rounded-lg border border-amber-700 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 disabled:opacity-40"
                    >
                      {recut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Scissors size={13} />}
                      חיתוך מחדש מהקרוסלה של אותו מאמר · חינם
                    </button>
                  )}
                  {slot.format !== "story" && <p className="mt-1">להרחבה: ״יצירה מחדש עם AI״ למטה, עם מספר שקופיות תוכן.</p>}
                </div>
              )}
              {recutNote && <p className="mt-1.5 text-[11px] text-emerald-700">{recutNote}</p>}
            </section>
          )}

          {/* Why it is not in Postiz. A failed send used to leave only a red
              chip; the reason — often Postiz's own rate limit, nothing to do
              with the post — was in the database and nowhere on screen. */}
          {slot.error && slot.status !== "posted" && (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-[11px] text-rose-900">
              השליחה האחרונה לפוסטיז נכשלה: {slot.error}
              {slot.status === "failed" && " · לשלוח שוב: ״אישור״ ואז ״שליחה לפוסטיז״."}
            </p>
          )}

          {/* What Postiz told us about this post: changed there, or published. */}
          {(slot.syncNote || slot.releaseUrl) && (
            <p className="rounded-lg bg-sky-50 px-3 py-2 text-[11px] text-sky-900">
              {slot.syncNote && <>{slot.syncNote}{slot.syncedAt ? ` · ${humanIl(slot.syncedAt)}` : ""}</>}
              {slot.releaseUrl && (
                <>
                  {slot.syncNote ? " · " : ""}
                  <a href={slot.releaseUrl} target="_blank" rel="noreferrer" className="underline">צפייה בפוסט שפורסם</a>
                </>
              )}
            </p>
          )}

          {published ? (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900">
              הפוסט הזה כבר פורסם. אי אפשר להזיז אותו — פוסט שיצא לאוויר משנים בפלטפורמה עצמה.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs">
                  תאריך
                  <input
                    type="date" value={day}
                    onChange={(e) => { setDay(e.target.value); setPostizNote(null); }}
                    className="rounded border border-line px-2 py-1"
                  />
                </label>
                <label className="flex items-center gap-1.5 text-xs">
                  שעה
                  <input
                    type="time" value={time}
                    onChange={(e) => { setTime(e.target.value); setPostizNote(null); }}
                    className="rounded border border-line px-2 py-1"
                  />
                </label>
                <span className="text-[11px] text-muted">שעון ישראל</span>
              </div>

              {slot.format === "story" ? (
                <p className="rounded-lg bg-canvas px-3 py-2 text-[11px] text-muted">
                  סטורי יוצא בלי כיתוב ובלי האשטגים, רק הפריימים. מה שכתוב על הפריימים נכתב מחדש עם AI למטה.
                </p>
              ) : (<>
              {/* The words. `slot.body`'s first line is the hook — the part
                  that shows before "עוד" — which is why the caption is a
                  textarea and not a one-line input. */}
              <label className="block">
                <span className="mb-1 block text-[11px] text-muted">
                  כיתוב · השורה הראשונה היא הוו
                </span>
                <textarea
                  value={body} rows={6} dir="rtl"
                  onChange={(e) => { setBody(e.target.value); setPostizNote(null); setIssues(null); }}
                  className="w-full rounded-lg border border-line bg-canvas px-3 py-2 text-sm leading-relaxed"
                />
                <span className="mt-0.5 block text-left text-[10px] text-muted">{body.length} תווים</span>
              </label>

              {/* Only where the platform HAS one. Showing a title box on an
                  Instagram carousel invites filling in a field that goes
                  nowhere. */}
              {(slot.platform === "youtube" || slot.format === "feed_link") && (
                <label className="block">
                  <span className="mb-1 block text-[11px] text-muted">כותרת</span>
                  <input
                    value={title} dir="rtl"
                    onChange={(e) => { setTitle(e.target.value); setPostizNote(null); setIssues(null); }}
                    className="w-full rounded-lg border border-line bg-canvas px-3 py-1.5 text-sm"
                  />
                </label>
              )}

              <label className="block">
                <span className="mb-1 block text-[11px] text-muted">האשטגים · מופרדים ברווח, בלי #</span>
                <input
                  value={tags} dir="rtl"
                  onChange={(e) => { setTags(e.target.value); setPostizNote(null); setIssues(null); }}
                  className="w-full rounded-lg border border-line bg-canvas px-3 py-1.5 text-sm"
                />
              </label>
              </>)}

              {/* THE REACH EXPERIMENT, and it is a property of the post, so
                  changing it on something already in Postiz re-creates it there
                  like any other edit. Instagram Reels only — nothing else can
                  be a trial. */}
              {slot.platform === "instagram" && slot.format === "reel" && (
                <label className="flex items-start gap-2 rounded-lg border border-line px-3 py-2">
                  <input
                    type="checkbox"
                    checked={trialReel}
                    onChange={(e) => { setTrialReel(e.target.checked); setPostizNote(null); }}
                    className="mt-0.5"
                  />
                  <span className="text-[11px] leading-relaxed">
                    <span className="font-medium text-ink">ריל ניסיון (Trial Reel)</span>
                    <br />
                    הריל מוצג קודם רק למי שלא עוקב אחרינו. אם הוא מצליח שם, אינסטגרם משחררת אותו
                    גם לעוקבים בעצמה. חייב להיות סרטון אחד בדיוק.
                  </span>
                </label>
              )}

              {/* SPENDS MONEY, so it says how much before it is pressed rather
                  than after. The steer is what makes this a tool instead of a
                  slot machine: without it the model draws again from the same
                  distribution and the operator presses until something lands. */}
              <div className="rounded-lg border border-dashed border-line p-2.5">
                <label className="block">
                  <span className="mb-1 block text-[11px] text-muted">
                    מה לא עבד? (לא חובה) — לדוגמה: ״יותר מדי מכירתי, תישאר על הכאב עצמו״
                  </span>
                  <input
                    value={steer} dir="rtl"
                    onChange={(e) => setSteer(e.target.value)}
                    className="w-full rounded-lg border border-line bg-canvas px-3 py-1.5 text-sm"
                  />
                </label>
                {isDeck && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                    <label className="flex items-center gap-1">
                      מטרת הפוסט
                      <select
                        value={goal}
                        onChange={(e) => setGoal(e.target.value as DeckGoalKey | "")}
                        className="rounded border border-line bg-canvas px-1.5 py-1"
                        title={DECK_GOAL_OPTIONS.find((g) => g.goal === goal)?.whenHe ?? "המודל בוחר לפי המאמר"}
                      >
                        <option value="">לפי המאמר{slot.deck ? ` (כרגע: ${slot.deck.goalLabelHe})` : ""}</option>
                        {DECK_GOAL_OPTIONS.map((g) => (
                          <option key={g.goal} value={g.goal}>{g.labelHe} · {g.whenHe}</option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-1">
                      שקופיות תוכן
                      <select
                        value={contentSlides}
                        onChange={(e) => setContentSlides(Number(e.target.value))}
                        className="rounded border border-line bg-canvas px-1.5 py-1"
                      >
                        <option value={0}>לפי המאמר (4 לפחות)</option>
                        {CONTENT_SLIDE_CHOICES.map((n) => (
                          <option key={n} value={n}>{n}{slot.deck?.suggestedContent === n && slot.deck.thin ? " · מומלץ" : ""}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={() => regenerate.mutate()}
                    disabled={regenerate.isPending || regenRunning}
                    className="flex items-center gap-1.5 rounded-lg border border-brand-700 px-2.5 py-1.5 text-xs font-medium text-brand-800 disabled:opacity-40"
                  >
                    {regenerate.isPending || regenRunning ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    {regenerate.isPending || regenRunning ? "כותב מחדש… (אפשר לסגור את החלון)" : "יצירה מחדש עם AI"}
                  </button>
                  <span className="text-[10px] text-muted">
                    $0.04–0.06 לפוסט · כותב מהמאמר עצמו · כ־25 שניות
                  </span>
                </div>
                {regenNote && <p className="mt-1.5 text-[11px] text-emerald-700">{regenNote}</p>}
              </div>

              {/* The caption as the server renders it for Postiz — body, CTA, link,
                  hashtags with #אלטרנבי. Of the SAVED post: unsaved edits are not
                  in it yet, and it says so rather than pretending. */}
              {slot.format !== "story" && (
              <details className="rounded-lg border border-line px-3 py-2">
                <summary className="cursor-pointer text-[11px] text-muted">
                  כך זה נשלח לפוסטיז{changed ? " (לפי הגרסה השמורה — יש שינויים שלא נשמרו)" : ""}
                </summary>
                <pre dir="rtl" className="mt-2 whitespace-pre-wrap font-sans text-xs leading-relaxed">{slot.caption}</pre>
              </details>
              )}

              {nextIso && timeChanged && (
                <p className="flex items-center gap-1.5 text-xs text-ink">
                  <ArrowLeftRight size={13} className="text-muted" />
                  {humanIl(slot.scheduledFor)} ← {humanIl(nextIso)}
                </p>
              )}

              {/* What this actually DOES, in the two cases where it differs. */}
              <p className="rounded-lg bg-canvas px-3 py-2 text-[11px] text-muted">
                {slot.postizPostId
                  ? "הפוסט כבר בפוסטיז. לפוסטיז אין עריכה של פוסט קיים, לכן שמירה מושכת אותו ויוצרת אותו מחדש. אם היצירה מחדש תיכשל — המשבצת תישאר ריקה, וזה ייאמר כאן במפורש."
                  : "הפוסט קיים רק אצלנו ועדיין לא נמסר לפוסטיז, אז השינוי נוגע רק ביומן שלנו."}
              </p>

              {seasonalNote && (
                <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {seasonalNote}
                </p>
              )}

              {(blackedOut || suggested) && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900">
                  <AlertTriangle size={13} className="shrink-0" />
                  {blackedOut
                    ? <span>לא מפרסמים בזמן הזה: {blackedOut.labelHe} (חסום עד {humanIl(blackedOut.endIso)}).</span>
                    : <span>השרת סירב: הזמן נופל בשבת/חג/יום זיכרון.</span>}
                  {suggested && (
                    <button
                      type="button"
                      onClick={() => { const p = israelParts(suggested); setDay(p.day); setTime(p.time); setSuggested(null); setError(null); }}
                      className="rounded border border-rose-300 bg-white px-2 py-0.5 font-medium hover:bg-rose-100"
                    >
                      להעביר ל־{humanIl(suggested)}
                    </button>
                  )}
                </div>
              )}

              {intoThePast && (
                <p className="flex items-start gap-1.5 text-xs text-amber-800">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  הזמן החדש כבר עבר. הדריפ שולח מה שמאושר וכבר הגיע זמנו, כך שהפוסט עלול לצאת בהרצה הקרובה.
                </p>
              )}

              {clash && (
                <p className="flex items-start gap-1.5 text-xs text-amber-800">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  פחות מ־3 שעות מ־{FORMAT_HE[clash.format] ?? clash.format} של
                  {" "}מאמר אחר, ״{clash.label ?? clash.campaign}״ ({humanIl(clash.scheduledFor)}).
                </p>
              )}
            </>
          )}

          {issues && issues.length > 0 && (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
              <p className="mb-1 font-medium">הבדיקות רצו מחדש על הטקסט החדש:</p>
              <ul className="space-y-0.5">
                {issues.map((i, n) => (
                  <li key={n}>
                    <span className={i.severity === "error" ? "font-medium text-danger" : ""}>
                      {i.severity === "error" ? "שגיאה" : "אזהרה"}
                    </span>
                    {" · "}{i.message}
                  </li>
                ))}
              </ul>
              <p className="mt-1 opacity-80">
                שגיאה מסמנת את הפוסט כלא־תקין, ומילוי היומן לא יתזמן אותו עד שיתוקן.
              </p>
            </div>
          )}

          {sendArmed && (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              הפוסט יישלח לפוסטיז <strong>מאושר</strong>, ויתפרסם אוטומטית ב{PLATFORM_HE[slot.platform]} ב־
              <strong>{humanIl(slot.scheduledFor)}</strong> — בלי שלב אישור נוסף בפוסטיז.
              {" "}
              <button onClick={() => setSendArmed(false)} className="underline">ביטול</button>
            </p>
          )}
          {sentNote && (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900">{sentNote}</p>
          )}

          {armed && (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900">
              {armed === "remove"
                ? "הפוסט יסומן כ״דולג״ וירד מהיומן. הוא לא ייצא, ומילוי יומן לא ישבץ אותו מחדש."
                : "התאריך יתבטל והפוסט יחזור למאגר כטיוטה בלי תאריך. מילוי יומן הבא יכול לשבץ אותו שוב."}
              {slot.postizPostId && " הפוסט נמצא בפוסטיז — הוא יימשך משם קודם, ורק אם זה יצליח יתעדכן כאן."}
              {" "}
              <button onClick={() => setArmed(null)} className="underline">ביטול</button>
            </p>
          )}

          {postizNote && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{postizNote}</p>
          )}
          {error && <p className="text-xs text-danger">{error}</p>}
        </div>

        <footer className="flex shrink-0 flex-wrap items-center gap-2 border-t border-line px-4 py-3">
          {/* Where to go and look at it. `/p/<id>` is Postiz's own preview of one
              post; `/launches` is its calendar, and the fallback when this post
              has not been handed over yet and has no id to point at. */}
          {postizAppUrl && (
            <a
              href={slot.postizPostId ? `${postizAppUrl}/p/${slot.postizPostId}` : `${postizAppUrl}/launches`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-xs text-muted underline decoration-dotted hover:text-ink"
            >
              <ExternalLink size={12} />
              {slot.postizPostId ? "צפייה בפוסטיז" : "היומן בפוסטיז"}
            </a>
          )}
          <span className="grow" />
          <button onClick={onClose} className="rounded-lg border border-line px-3 py-2 text-sm">
            ביטול
          </button>
          {!published && (
            armed ? (
              <button
                onClick={() => takeOff.mutate(armed)}
                disabled={takeOff.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-rose-700 px-3 py-2 text-xs font-medium text-white disabled:opacity-40"
              >
                {takeOff.isPending && <Loader2 size={13} className="animate-spin" />}
                {armed === "remove" ? "לאשר הסרה" : "לאשר ביטול תזמון"}
              </button>
            ) : (
              <>
                <button
                  onClick={() => setArmed("unschedule")}
                  title="התאריך מתבטל והפוסט חוזר למאגר כטיוטה בלי תאריך. מילוי יומן הבא יכול לשבץ אותו מחדש."
                  className="rounded-lg border border-line px-2.5 py-2 text-xs text-muted hover:text-ink"
                >
                  ביטול תזמון
                </button>
                <button
                  onClick={() => setArmed("remove")}
                  title="הפוסט לא ייצא. מסומן כ״דולג״, יורד מהיומן, ומילוי יומן לא ישבץ אותו שוב. הפיך."
                  className="rounded-lg border border-rose-300 px-2.5 py-2 text-xs text-rose-800 hover:bg-rose-50"
                >
                  הסרה מהיומן
                </button>
              </>
            )
          )}

          {/* Only where it is the next real step. A post already approved or in
              Postiz has passed this gate; one whose checks failed cannot. */}
          {!published && slot.status !== "approved" && slot.status !== "scheduled" && (
            <button
              onClick={() => approve.mutate()}
              disabled={approve.isPending || changed || !slot.checksOk}
              title={
                !slot.checksOk
                  ? "הבדיקות נכשלו — צריך לתקן את הטקסט קודם."
                  : changed
                    ? "יש שינויים שלא נשמרו. לשמור קודם."
                    : "אישור כאן בלבד. אחריו לוחצים ״שליחה לפוסטיז״ כדי שיתוזמן שם."
              }
              className="flex items-center gap-1.5 rounded-lg border border-emerald-600 px-3 py-2 text-sm font-medium text-emerald-800 disabled:opacity-40"
            >
              {approve.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              אישור
            </button>
          )}
          {/* After approval, before Postiz holds it. The reason it is disabled is
              said on hover, because a disabled send with no reason looks broken. */}
          {slot.status === "approved" && !slot.postizPostId && (
            <button
              onClick={() => (sendArmed ? sendScheduled.mutate() : setSendArmed(true))}
              disabled={!canSend || sendScheduled.isPending}
              title={
                changed ? "יש שינויים שלא נשמרו. לשמור קודם."
                  : !slotInFuture ? "זמן הפרסום עבר או קרוב מדי — הזיזו אותו קודם."
                  : "שליחה לפוסטיז כפוסט מאושר, שיתפרסם אוטומטית בזמן שלו."
              }
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-40 ${
                sendArmed ? "bg-emerald-800" : "bg-emerald-700"
              }`}
            >
              {sendScheduled.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sendArmed ? "לאשר שליחה ופרסום" : "שליחה לפוסטיז"}
            </button>
          )}
          <button
            onClick={() => move.mutate()}
            disabled={published || !changed || move.isPending || Boolean(blackedOut)}
            title={!changed ? "לא שינית כלום." : ""}
            className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {move.isPending && <Loader2 size={14} className="animate-spin" />}
            שמירה
          </button>
        </footer>
      </div>
    </div>
  );
}
